import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "./useProfile";
import { useScripts } from "./useScripts";

interface TierLimits {
  maxScripts: number;
  maxCharacters: number;
  mirrorMode: boolean;
}

interface DatabaseLimits {
  maxScripts?: number;
  maxCharactersPerScript?: number;
  hasMirrorMode?: boolean;
}

// 預設限制（資料庫無法訪問時的最終回退值）
const FALLBACK_LIMITS: TierLimits = {
  maxScripts: 3,
  maxCharacters: Infinity,
  mirrorMode: false,
};

export function useScriptLimits() {
  const { getSubscriptionTier } = useProfile();
  const { scripts } = useScripts();

  const tier = getSubscriptionTier();

  // 從資料庫獲取方案限制（包括 Free）
  const { data: planLimits } = useQuery({
    queryKey: ["script-limits", tier],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("limits")
        .eq("name", tier)
        .eq("is_active", true)
        .maybeSingle();

      if (error || !data?.limits) {
        return FALLBACK_LIMITS;
      }

      const dbLimits = data.limits as DatabaseLimits;
      
      return {
        maxScripts: dbLimits.maxScripts === -1 ? Infinity : (dbLimits.maxScripts ?? FALLBACK_LIMITS.maxScripts),
        maxCharacters: dbLimits.maxCharactersPerScript === -1 ? Infinity : (dbLimits.maxCharactersPerScript ?? Infinity),
        mirrorMode: dbLimits.hasMirrorMode ?? false,
      };
    },
    staleTime: 1000 * 60 * 10, // 10 分鐘快取
    enabled: !!tier,
  });

  const limits = planLimits || FALLBACK_LIMITS;
  const currentScriptCount = scripts.length;
  const canCreateScript = currentScriptCount < limits.maxScripts;

  const getCharacterLimit = () => limits.maxCharacters;
  const getScriptLimit = () => limits.maxScripts;
  const canUseMirrorMode = () => limits.mirrorMode;

  const getScriptUsagePercentage = () => {
    if (limits.maxScripts === Infinity) return 0;
    return Math.round((currentScriptCount / limits.maxScripts) * 100);
  };

  const getCharacterUsagePercentage = (characterCount: number) => {
    if (limits.maxCharacters === Infinity) return 0;
    return Math.round((characterCount / limits.maxCharacters) * 100);
  };

  const isApproachingCharacterLimit = (characterCount: number) => {
    if (limits.maxCharacters === Infinity) return false;
    return characterCount >= limits.maxCharacters * 0.9;
  };

  const isOverCharacterLimit = (characterCount: number) => {
    if (limits.maxCharacters === Infinity) return false;
    return characterCount > limits.maxCharacters;
  };

  const formatLimit = (limit: number) => {
    if (limit === Infinity) return "∞";
    return limit.toLocaleString();
  };

  return useMemo(() => {
    const getCharacterLimit = () => limits.maxCharacters;
    const getScriptLimit = () => limits.maxScripts;
    const canUseMirrorMode = () => limits.mirrorMode;

    const getScriptUsagePercentage = () => {
      if (limits.maxScripts === Infinity) return 0;
      return Math.round((currentScriptCount / limits.maxScripts) * 100);
    };

    const getCharacterUsagePercentage = (characterCount: number) => {
      if (limits.maxCharacters === Infinity) return 0;
      return Math.round((characterCount / limits.maxCharacters) * 100);
    };

    const isApproachingCharacterLimit = (characterCount: number) => {
      if (limits.maxCharacters === Infinity) return false;
      return characterCount >= limits.maxCharacters * 0.9;
    };

    const isOverCharacterLimit = (characterCount: number) => {
      if (limits.maxCharacters === Infinity) return false;
      return characterCount > limits.maxCharacters;
    };

    const formatLimit = (limit: number) => {
      if (limit === Infinity) return "∞";
      return limit.toLocaleString();
    };

    return {
      tier,
      limits,
      currentScriptCount,
      canCreateScript,
      getCharacterLimit,
      getScriptLimit,
      canUseMirrorMode,
      getScriptUsagePercentage,
      getCharacterUsagePercentage,
      isApproachingCharacterLimit,
      isOverCharacterLimit,
      formatLimit,
    };
  }, [tier, limits, currentScriptCount, canCreateScript]);
}
